'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const steps = [
{
  number: '01',
  icon: 'MagnifyingGlassIcon',
  title: 'Browse & Filter',
  description: 'Search thousands of templates by platform, category, price range, and style. Use advanced filters to narrow down exactly what you need.',
  color: '#E8633A'
},
{
  number: '02',
  icon: 'CreditCardIcon',
  title: 'Secure Checkout',
  description: 'Purchase with confidence using our encrypted checkout. One-time payment, no subscriptions. Instant access after payment.',
  color: '#21759B'
},
{
  number: '03',
  icon: 'KeyIcon',
  title: 'Get Your License Key',
  description: 'Every purchase includes a unique license key registered to your account. Use it to verify ownership and unlock premium support.',
  color: '#96BF48'
},
{
  number: '04',
  icon: 'ArrowDownTrayIcon',
  title: 'Download Forever',
  description: 'Access your purchases anytime from your dashboard. Download original files, future updates, and documentation whenever you need.',
  color: '#7F54B3'
}];


export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32"
      style={{ background: 'var(--bg-secondary)' }}>

      <div className="max-w-8xl mx-auto px-6">
        <div className="reveal text-center mb-16">
          <span
            className="text-2xs font-bold uppercase tracking-[0.2em] mb-3 block"
            style={{ color: 'var(--accent)' }}>

            Simple Process
          </span>
          <h2
            className="font-display font-light text-4xl lg:text-6xl"
            style={{ color: 'var(--text-primary)' }}>

            From browse to{' '}
            <span className="italic">live in hours.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps.map((step, i) =>
          <div
            key={step.number}
            className="reveal rounded-2xl p-6 relative"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              transitionDelay: `${i * 100}ms`
            }}>

              {/* Connector line */}
              {i < steps.length - 1 &&
            <div
              className="hidden lg:block absolute top-10 left-full w-6 h-px z-10"
              style={{ background: 'var(--border)' }}
              aria-hidden="true" />

            }
              <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
              style={{ background: `${step.color}18`, color: step.color }}>

                <Icon name={step.icon as 'KeyIcon'} size={22} />
              </div>
              <span
              className="font-display text-5xl font-light block mb-3"
              style={{ color: `${step.color}30` }}>

                {step.number}
              </span>
              <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {step.description}
              </p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div
          className="reveal rounded-3xl p-10 lg:p-16 text-center overflow-hidden relative"
          style={{
            background: 'linear-gradient(135deg, var(--accent) 0%, #C44A20 60%, #8B3015 100%)'
          }}>

          <div
            className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ background: 'rgba(255,200,150,0.5)' }}
            aria-hidden="true" />

          <div className="relative z-10">
            <h2 className="font-display font-light text-4xl lg:text-5xl text-white mb-4">
              Ready to launch your{' '}
              <span className="italic">next project?</span>
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">Join 48,000+ designers and developers who ship faster with ZyForge.

            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                href="/templates"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white text-sm font-semibold transition-transform hover:scale-105"
                style={{ color: 'var(--accent)' }}>

                Browse Templates
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
              <Link
                href="/sign-up-login"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold text-white border border-white/30 transition-all hover:bg-white/10">

                Create Free Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>);

}